import { Injectable, inject } from '@angular/core';
import { ValidationTranslatorService } from './validation-translator.service';
import { TripService } from './trip.service';
import { ValidationIssue, ValidationReport } from '../types/validation';
import { HttpClient } from '@angular/common/http';
import { RoadStop } from '../models/transport';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SectionValidationService {
  private validationTranslator = inject(ValidationTranslatorService);
  private tripService = inject(TripService);
  private http = inject(HttpClient);

  /**
   * Get current validation report
   */
  private getCurrentReport() {
    return this.tripService.validationReport();
  }

  /**
   * Helper to count issues by severity
   */
  private countBySeverity(
    issues: ValidationIssue[],
    severity: 'error' | 'warning',
  ): number {
    return issues.filter((i) => i.severity === severity).length;
  }

  /**
   * Get validation errors for a specific section
   */
  getErrorsBySection(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
  ): number {
    const report = this.getCurrentReport();
    if (!report) return 0;

    const sectionIssues = report.issues.filter(
      (issue) => issue.scope === scope,
    );
    return this.countBySeverity(sectionIssues, 'error');
  }

  /**
   * Get validation warnings for a specific section
   */
  getWarningsBySection(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
  ): number {
    const report = this.getCurrentReport();
    if (!report) return 0;

    const sectionIssues = report.issues.filter(
      (issue) => issue.scope === scope,
    );
    return this.countBySeverity(sectionIssues, 'warning');
  }

  /**
   * Check if a section has any validation issues
   */
  hasIssuesInSection(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
  ): boolean {
    const report = this.getCurrentReport();
    if (!report) return false;

    return report.issues.some((issue) => issue.scope === scope);
  }

  /**
   * Get all issues for a specific section with translated messages
   */
  getIssuesForSection(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
  ): Array<ValidationIssue & { translatedMessage: string }> {
    const report = this.getCurrentReport();
    if (!report) return [];

    return report.issues
      .filter((issue) => issue.scope === scope)
      .map((issue) => ({
        ...issue,
        translatedMessage: this.validationTranslator.translateIssue(issue),
      }));
  }

  /**
   * Get validation status for a section ('valid', 'warnings', 'errors')
   */
  getSectionStatus(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
  ): 'valid' | 'warnings' | 'errors' {
    const errors = this.getErrorsBySection(scope);
    const warnings = this.getWarningsBySection(scope);

    if (errors > 0) return 'errors';
    if (warnings > 0) return 'warnings';
    return 'valid';
  }

  /**
   * Get validation issues for a specific item in a section
   */
  getIssuesForItem(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
    index: number,
  ): Array<ValidationIssue & { translatedMessage: string }> {
    const report = this.getCurrentReport();
    if (!report) return [];

    return report.issues
      .filter((issue) => issue.scope === scope && issue.index === index)
      .map((issue) => ({
        ...issue,
        translatedMessage: this.validationTranslator.translateIssue(issue),
      }));
  }

  /**
   * Check if a specific field in an item has validation issues
   */
  hasFieldIssue(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
    index: number,
    field: string,
  ): boolean {
    const issues = this.getIssuesForItem(scope, index);
    return issues.some((issue) => issue.fieldPath === field);
  }

  /**
   * Get field-specific validation messages
   */
  getFieldIssues(
    scope: 'transport' | 'accommodation' | 'activity' | 'meal',
    index: number,
    field: string,
  ): Array<ValidationIssue & { translatedMessage: string }> {
    const issues = this.getIssuesForItem(scope, index);
    return issues.filter((issue) => issue.fieldPath === field);
  }

  validateItinerary(
    roadStops: RoadStop[],
    transportIndex: number,
    tripId: string,
  ) {
    const apiUrl = `${environment.apiUrl}/trips/${tripId}/validate-itinerary`;
    return this.http
      .post<ValidationReport>(apiUrl, { roadStops, transportIndex })
      .pipe(
        tap((report) => {
          this.tripService.validationReport.set(report);
        }),
      );
  }
}
